import { useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';

import type { IssueCard } from '@tasks/contracts';

import { EmptyState } from '../../components/EmptyState';
import { formatPoints } from '../../lib/format';
import { cn } from '../../lib/cn';
import { BacklogIssueRow } from './BacklogIssueRow';
import { groupByEpic, totalPoints } from './backlogModel';
import styles from './BacklogSection.module.css';

export type BacklogSectionProps = {
  readonly issues: readonly IssueCard[];
  readonly canManage: boolean;
  readonly isSelected: (issueId: string) => boolean;
  readonly onOpenIssue: (key: string) => void;
  readonly onToggleSelect: (issueId: string, event: { readonly shiftKey: boolean; readonly metaKey: boolean }) => void;
};

/** Бэклог без спринта + группировка по эпикам с прогрессом (UC-SPR-01). */
export function BacklogSection({
  issues,
  canManage,
  isSelected,
  onOpenIssue,
  onToggleSelect,
}: BacklogSectionProps) {
  const { setNodeRef, isOver } = useDroppable({ id: 'backlog', data: { sprintId: null } });
  const groups = groupByEpic(issues);

  return (
    <section
      ref={setNodeRef}
      className={cn(styles['section'], isOver && styles['over'])}
      aria-label="Бэклог без спринта"
    >
      <header className={styles['header']}>
        <Inbox aria-hidden size={16} />
        <h2 className={styles['title']}>Бэклог</h2>
        <span className={styles['summary']}>
          {formatPoints(totalPoints(issues))} sp · {issues.length}
        </span>
      </header>

      {issues.length === 0 ? (
        <EmptyState
          compact
          icon={<Inbox size={18} />}
          title="Бэклог пуст"
          description="Создайте задачу — она появится здесь."
        />
      ) : (
        groups.map((group) => (
          <div key={group.epic?.id ?? '__none__'} className={styles['group']}>
            <div className={styles['groupHeader']}>
              {group.epic ? <ChevronDown aria-hidden size={14} /> : <ChevronRight aria-hidden size={14} />}
              <span className={styles['epicName']}>{group.epic ? group.epic.summary : 'Без эпика'}</span>
              <span className={styles['epicMeta']}>
                {group.doneCount}/{group.issues.length} · {formatPoints(group.totalPoints)} sp
              </span>
            </div>
            <ul className={styles['list']}>
              {group.issues.map((issue) => (
                <li key={issue.id}>
                  <BacklogIssueRow
                    issue={issue}
                    isSelected={isSelected(issue.id)}
                    onOpen={onOpenIssue}
                    onToggleSelect={onToggleSelect}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))
      )}

      {canManage ? null : <p className={styles['readonlyNote']}>Изменять бэклог может администратор проекта.</p>}
    </section>
  );
}
